import './filesWithPCIData.less'
import { ACTION_FETCH_PCI_FILES_DATA, fetchPCIFilesData } from '../../../complianceDashboardSlice'
import LoaderView from '../../../../../../components/Loader/loader'
import { getShowLoader } from '../../../../../../reducers/requestReducer'
import { RootState } from '../../../../../../rootReducer'
import {
  UNIQUE_WIDGET_INDEX,
  getUniqueWidgetIndex,
  ChartColors
} from '../../../../mainDashboardv2/constants'
import { AttributeSetData, PCIFileData } from '../../../queries'
import { mockFilesWithPCIData } from '../../mocks'
import WidgetTitle from '../../../../../../components/WidgetTitle'
import Card from '../../../../../accessControl/components/card'
import ChartPie, { ChartPieItem } from '../../../../../../components/ChartPie/chartPie'
import { getUpdatedUrl } from '../../../../../../utils/urlUtil'
import {
  DATA_SOURCE_TYPE,
  FILTER_FILE_SENSITIVITY_VALUES,
  FILTER_SENSITIVITY,
  URL_ATTRIBUTES_SET,
  URL_DATA_SOURCES,
  URL_DOCUMENTS_ALL,
  URL_DOCUMENTS_CLASSIFIED,
  URL_INSIGHTS,
  URL_OVERVIEW
} from '../../../../../../constants'
import historyService from '../../../../../../services/historyService'
import { EmptyState } from '../../emptyState/emptyState'
import { hasData } from '../../helper'
import EmptyStateIcon from '../../../../../../assets/inlineSvg/empty-file-search-icon.svg'
import { useDashboardType } from '../../hooks/useDashboardType'
import { DashboardCategory } from '../../../../mainDashboardv2/slice'
import { ComplianceDashboardTypes } from '../../../constants'
import { Typography, Icon, Tooltip, Button } from '@lightbeamai/design-system'
import React, { useEffect, useMemo } from 'react'
import { FormattedNumber, useIntl } from 'react-intl'
import { connect } from 'react-redux'

interface OwnProps {
  useMockData?: boolean
  widgetDatasourceId?: string
  widgetDashboardId?: string
  widgetDashboardType?: string
  attributeSetId?: string
}

interface StateProps {
  widget?: PCIFileData | null
  showLoader: boolean
  attributeSetData?: AttributeSetData | null
}

interface DispatchProps {
  fetchPCIFilesData: (params: { attributeSetId: string; [UNIQUE_WIDGET_INDEX]?: string }) => void
}

interface ListItem {
  key: string
  value: number
  color: string
  datasourceId?: string
  datasourceType?: string
  isOthers?: boolean
}
interface ExtendedChartPieItem extends ChartPieItem {
  datasourceId?: string
  datasourceType?: string
  isOthers?: boolean
}

type FilesWithPCIDataComponentProps = StateProps & DispatchProps & OwnProps

const FilesWithPCIData = (props: FilesWithPCIDataComponentProps) => {
  const {
    widget,
    showLoader,
    useMockData = false,
    widgetDatasourceId,
    widgetDashboardId,
    widgetDashboardType,
    attributeSetData,
    fetchPCIFilesData
  } = props

  const intl = useIntl()

  const { isPciDashboard } = useDashboardType(widgetDashboardType as DashboardCategory)

  useEffect(() => {
    if (useMockData) return

    if (attributeSetData?.id) {
      fetchPCIFilesData({
        attributeSetId: attributeSetData.id,
        [UNIQUE_WIDGET_INDEX]: getUniqueWidgetIndex(widgetDashboardId, widgetDatasourceId)
      })
    }
  }, [useMockData, fetchPCIFilesData, attributeSetData, widgetDashboardId, widgetDatasourceId])

  const pieChartData = useMemo((): ExtendedChartPieItem[] => {
    if (!widget?.datasources?.length) return []

    const sorted = [...widget.datasources].sort((a, b) => b.objectsCount - a.objectsCount)
    const top4 = sorted.slice(0, 4)
    const others = sorted.slice(4)

    const top4Data: ExtendedChartPieItem[] = top4.map((source, index) => ({
      key: source.name,
      value: source.objectsCount,
      color:
        [ChartColors.Purple, ChartColors.LightPurple, ChartColors.Blue, ChartColors.LightBlue][
          index
        ] || ChartColors.Purple,
      datasourceId: source.id,
      datasourceType: source.type,
      isOthers: false
    }))

    if (others.length > 0) {
      const othersCount = others.reduce((sum, item) => sum + item.objectsCount, 0)
      top4Data.push({
        key: intl.formatMessage({ id: 'others' }),
        value: othersCount,
        color: ChartColors.Yellow,
        isOthers: true
      })
    }

    return top4Data
  }, [widget, intl])

  const listData = useMemo((): ListItem[] => {
    if (!widget?.datasources?.length) return []

    const sorted = [...widget.datasources].sort((a, b) => b.objectsCount - a.objectsCount)
    const top4 = sorted.slice(0, 4)
    const others = sorted.slice(4)

    const top4ListData: ListItem[] = top4.map((source, index) => ({
      key: source.name,
      value: source.objectsCount,
      color:
        [ChartColors.Purple, ChartColors.LightPurple, ChartColors.Blue, ChartColors.LightBlue][
          index
        ] || ChartColors.Purple,
      datasourceId: source.id,
      datasourceType: source.type,
      isOthers: false
    }))

    if (others.length > 0) {
      const othersCount = others.reduce((sum, item) => sum + item.objectsCount, 0)
      top4ListData.push({
        key: intl.formatMessage({ id: 'others' }),
        value: othersCount,
        color: ChartColors.Yellow,
        isOthers: true
      })
    }

    return top4ListData
  }, [widget, intl])

  const handlePieClick = (key: string) => {
    const item = pieChartData.find((pieItem) => pieItem.key === key)
    if (item && !item.isOthers && item.datasourceId) {
      historyService.push(
        getUpdatedUrl({
          pathName: `${URL_DATA_SOURCES}/${item.datasourceId}${URL_DOCUMENTS_ALL}`,
          pageName: URL_DOCUMENTS_CLASSIFIED,
          queryParams: {
            [DATA_SOURCE_TYPE]: item.datasourceType || ''
          },
          pageQueryParams: {
            [FILTER_SENSITIVITY]: FILTER_FILE_SENSITIVITY_VALUES.sensitive
          },
          resetQuery: true,
          resetPageQuery: true
        })
      )
    }
  }

  const handleListItemClick = (item: ListItem) => {
    if (item.isOthers || !item.datasourceId) return
    historyService.push(
      getUpdatedUrl({
        pathName: `${URL_DATA_SOURCES}/${item.datasourceId}${URL_DOCUMENTS_ALL}`,
        pageName: URL_DOCUMENTS_CLASSIFIED,
        queryParams: {
          [DATA_SOURCE_TYPE]: item.datasourceType || ''
        },
        pageQueryParams: {
          [FILTER_SENSITIVITY]: FILTER_FILE_SENSITIVITY_VALUES.sensitive
        },
        resetQuery: true,
        resetPageQuery: true
      })
    )
  }

  const handleViewAll = () => {
    if (!attributeSetData?.id) return

    historyService.push(
      getUpdatedUrl({
        pathName: `${URL_INSIGHTS}${URL_ATTRIBUTES_SET}/${attributeSetData.id}${URL_OVERVIEW}`
      })
    )
  }

  const handleSummaryItemClick = (tabUrl: string) => {
    if (!attributeSetData?.id) return

    historyService.push(
      getUpdatedUrl({
        pathName: `${URL_INSIGHTS}${URL_ATTRIBUTES_SET}/${attributeSetData.id}${tabUrl}`
      })
    )
  }

  const cardTitle = (
    <WidgetTitle
      title={
        <div className="widget-title">
          <Typography color="primary" variant="h6" as="span">
            {intl.formatMessage(
              {
                id: 'compliance.dashboard.pci.widget.filesWithPCIData.title'
              },
              {
                type: isPciDashboard ? ComplianceDashboardTypes.PCI : ComplianceDashboardTypes.HIPAA
              }
            )}
          </Typography>
          <Tooltip
            title={intl.formatMessage({
              id: isPciDashboard
                ? 'compliance.dashboard.pci.widget.title.tooltip'
                : 'compliance.dashboard.hipaa.widget.title.tooltip'
            })}
            placement="right"
          >
            <Icon size={16} name="InfoCircle" className="pointer" />
          </Tooltip>
        </div>
      }
      bordered
    />
  )

  const hasChartData = hasData(pieChartData)

  return (
    <Card
      className="files-with-pci-data-widget"
      cardTitle={cardTitle}
      cardFooter={
        hasChartData ? (
          <Button
            className="card-footer"
            size={24}
            variant="link"
            icon={<Icon name="ArrowRight" />}
            iconPosition="right"
            onClick={handleViewAll}
          >
            {intl.formatMessage({ id: 'notifications.viewAll' })}
          </Button>
        ) : null
      }
    >
      <div className="widget-content">
        <div className="summary-section">
          <div
            className="summary-item"
            onClick={() => handleSummaryItemClick(URL_OVERVIEW)}
            style={{ cursor: 'pointer' }}
          >
            <Typography color="primary" type="strong" variant="h4">
              <FormattedNumber value={widget?.instancesInFilesCount || 0} notation="compact" />
            </Typography>
            <Typography color="secondary" type="medium" variant="baseMinus2" as="span">
              {intl.formatMessage({
                id:
                  'compliance.dashboard.pci.widget.filesWithPCIData.summary.totalAttributeInstances'
              })}
            </Typography>
          </div>
          <div
            className="summary-item"
            onClick={() => handleSummaryItemClick(URL_DATA_SOURCES)}
            style={{ cursor: 'pointer' }}
          >
            <Typography color="primary" type="strong" variant="h4">
              <FormattedNumber value={widget?.datasources?.length || 0} notation="compact" />
            </Typography>
            <Typography color="secondary" type="medium" variant="baseMinus2" as="span">
              {intl.formatMessage({
                id: 'compliance.dashboard.pci.widget.filesWithPCIData.summary.datasources'
              })}
            </Typography>
          </div>
        </div>

        {hasChartData ? (
          <div className="chart-section">
            <Typography as="span" variant="baseMinus2">
              {intl.formatMessage({
                id: 'compliance.dashboard.pci.widget.filesWithPCIData.chartTitle'
              })}
            </Typography>

            <div className="chart-container">
              <div className="pie-chart-wrapper">
                <ChartPie
                  data={pieChartData}
                  width={180}
                  gap={2}
                  isAnimated={true}
                  isClickable={true}
                  onClick={handlePieClick}
                  centerCountStyles={{
                    fontSize: '1.4rem',
                    fontWeight: 700
                  }}
                />
              </div>

              <div className="legend">
                {listData.map((item) => (
                  <div
                    key={item.key}
                    className={`legend-item ${item.isOthers ? 'others-item' : 'clickable-item'}`}
                    onClick={() => handleListItemClick(item)}
                    style={{ cursor: item.isOthers ? 'default' : 'pointer' }}
                  >
                    <div className="circle" style={{ backgroundColor: item.color }} />
                    <Typography variant="baseMinus2" color="primary" as="span" className="xs-ml-4">
                      {item.key}
                    </Typography>
                    <Typography variant="baseMinus2" type="strong" className="value">
                      {intl.formatNumber(item.value, { notation: 'compact' })}
                    </Typography>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <EmptyState
            title={intl.formatMessage({
              id: 'compliance.dashboard.pci.widget.filesWithPCIData.errorText'
            })}
            icon={<EmptyStateIcon />}
          />
        )}
      </div>

      <LoaderView size="small" showLoader={showLoader} />
    </Card>
  )
}

const mapStateToProps = (state: RootState, ownProps: OwnProps): StateProps => {
  const widgetKey = getUniqueWidgetIndex(ownProps.widgetDashboardId, ownProps.widgetDatasourceId)

  return {
    widget: ownProps.useMockData
      ? mockFilesWithPCIData.widget
      : state.complianceDashboard.pciFilesData?.[widgetKey] || null,
    attributeSetData: state.complianceDashboard.attributeSetData,
    showLoader: getShowLoader(ACTION_FETCH_PCI_FILES_DATA)
  }
}

const mapDispatchToProps: DispatchProps = {
  fetchPCIFilesData
}

export default connect(mapStateToProps, mapDispatchToProps)(FilesWithPCIData)
